import _ from 'underscore';
import { getCreatorApiUrl } from './api-urls';
import auth from './auth';
import axios, { AxiosProgressEvent } from 'axios';
import { RawWorkout } from '../new/pages/collection/workouts/workouts.domain';
import {
  ApiAdminProgramUpsertRequest,
  ApiInfluencerUpsertRequest,
} from '@client/new/api/types';
import { FullMedia } from '@client/new/common/videos/GQLTypes';
import { sanitizeUploadFilename } from '@client/new/common/videos/videos.helpers';
import { Tag, WorkoutTiny } from '../../generated/graphql';

const getRestConfig = (data?: Object) => ({
  headers: {
    Authorization: auth.getToken(),
  },
  data,
});

const mapError = (err: { status: number }) => {
  if (err.status === 401) {
    throw new Error('Unauthorized');
  } else if (err.status === 403) {
    throw new Error('Forbidden');
  } else if (err.status === 404) {
    throw new Error('Item not found');
  }

  throw err;
};

const getInfluencers = async (id: number, mode = undefined) => {
  let url = '/admin-influencers';
  if (id) {
    url += `/${id ? id : ''}`;
  } else if (mode) {
    url += `/admin-influencers?mode=${mode}`;
  }
  const endpoint = getCreatorApiUrl(url);
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const upsertInfluencer = async (postData: ApiInfluencerUpsertRequest) => {
  const endpoint = getCreatorApiUrl('/admin-influencers');
  const config = getRestConfig();

  try {
    const { data } = await axios.post(endpoint, postData, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const deleteInfluencer = async (id: number) => {
  const endpoint = getCreatorApiUrl('/admin-influencers');
  const config = getRestConfig({ id });

  try {
    const { data } = await axios.delete(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const getWorkout = async (id: number) => {
  const endpoint = getCreatorApiUrl(`/admin-workouts/${id}`);
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const cloneWorkout = async (id: number, delta?: Partial<RawWorkout>) => {
  const endpoint = getCreatorApiUrl(`/admin-workouts/${id}/clone`);
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    await upsertWorkout({ ...data, ...delta });

    return data;
  } catch (error) {
    mapError(error);
  }
};

const archiveWorkout = async (id: number) => {
  const endpoint = getCreatorApiUrl(`/admin-workouts/${id}/archive`);
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const getProgramList = async (influencerId: number) => {
  const endpoint = getCreatorApiUrl(
    `/admin-programs?influencerId=${influencerId}`,
  );
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const getProgram = async (programId: number) => {
  const endpoint = getCreatorApiUrl(`/admin-programs/${programId}`);
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const upsertProgram = async (postData: ApiAdminProgramUpsertRequest) => {
  const endpoint = getCreatorApiUrl('/admin-programs');
  const config = getRestConfig();

  try {
    const { data } = await axios.post(endpoint, postData, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const reorderProgram = async (programId: number, workoutIds: Array<number>) => {
  const endpoint = getCreatorApiUrl(`/admin-programs/${programId}/reorder`);
  const config = getRestConfig();

  try {
    const { data } = await axios.post(endpoint, { workoutIds }, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const upsertWorkout = async (
  postData: Partial<WorkoutTiny>,
): Promise<RawWorkout> => {
  const endpoint = getCreatorApiUrl('/admin-workouts');
  const config = getRestConfig();

  try {
    const { data } = await axios.post(endpoint, postData, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const deleteWorkout = async (id: number) => {
  const endpoint = getCreatorApiUrl('/admin-workouts');
  const config = getRestConfig({ id });

  try {
    const { data } = await axios.delete(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

export type PortalUser = {
  id: number;
  username: string;
  role: number;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  influencerId: number;
};

const getPortalUsers = async (id?: number) => {
  const endpoint = getCreatorApiUrl(`/admin-portal-users/${id ? id : ''}`);
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const upsertPortalUser = async (postData: PortalUser) => {
  const endpoint = getCreatorApiUrl('/admin-portal-users');
  const config = getRestConfig();

  try {
    const { data } = await axios.post(endpoint, postData, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const deletePortalUser = async (id: number) => {
  const endpoint = getCreatorApiUrl('/admin-portal-users');
  const config = getRestConfig({ id });

  try {
    const { data } = await axios.delete(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

type UploadConfig = {
  skipCompression?: boolean;
  onProgress?: (x: number) => void;
  creatorId?: number;
  tags?: string[];
  source?: string;
};

function trackUploadProgress(onChange: (x: number) => void) {
  return function (evt: AxiosProgressEvent) {
    if (evt.lengthComputable) {
      const percentComplete = evt.loaded / evt.total;
      onChange(percentComplete);
    }
  };
}

const upload = async (
  file: File,
  creatorId: number,
  {
    skipCompression = false,
    onProgress = () => {},
    tags = [],
  }: UploadConfig = {},
): Promise<FullMedia> => {
  const adminUploadUrl = `${
    import.meta.env.VITE_ADMIN_UPLOAD_BASE_URL
  }/admin-upload`;

  // NOTE: this is to bypass chrome bug related to react-dropzone:
  // https://github.com/react-dropzone/react-dropzone/issues/1290#issuecomment-1665516575
  const blob = await fetch(URL.createObjectURL(file)).then((r) => r.blob());
  var formData = new FormData();
  const fixedFile = new File([blob], sanitizeUploadFilename(file.name), {
    type: file.type,
  });
  formData.append('media', fixedFile);

  const format = file.type.startsWith('image')
    ? 'image'
    : file.type.startsWith('audio')
    ? 'audio'
    : 'video';

  const url = new URL(adminUploadUrl);
  url.searchParams.append('creatorId', String(creatorId));
  url.searchParams.append('compress', skipCompression ? 'skip' : 'true');
  url.searchParams.append('format', format);

  try {
    const { data } = await axios.post<FullMedia>(url.toString(), formData, {
      headers: {
        Authorization: auth.getToken(),
      },
      timeout: 600000,
      onUploadProgress: onProgress
        ? trackUploadProgress(onProgress)
        : undefined,
    });

    return data;
  } catch (error) {
    mapError(error);
  }
};

const upsertCustomColorSchema = async (
  influencerId: number,
  postData: Object,
) => {
  const endpoint = getCreatorApiUrl(
    `/admin-influencers/${influencerId}/color-schema`,
  );
  const config = getRestConfig();

  try {
    const { data } = await axios.post(endpoint, postData, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const getConfigFile = async () => {
  const endpoint = getCreatorApiUrl('/admin/config-file');
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const updateConfigFile = async (configFile: Object) => {
  const endpoint = getCreatorApiUrl('/admin/config-file');
  const config = getRestConfig();

  try {
    const { data } = await axios.put(endpoint, { configFile }, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

export enum FreeSubscriptionType {
  ANNUAL = 'ANNUAL',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
}

enum SourceClassification { // TODO: move to common-enums
  PaidSubscription,
  GiftedAnnualSubscription,
  FreeSubscription,
  MigratedSubscription,
  SideDealSubscription,
}

const generateFreeSubscription = async (dto: {
  email: string;
  subscriptionType: FreeSubscriptionType;
  sourceInfluencerId?: number;
  note?: string;
}) => {
  const endpoint = getCreatorApiUrl(`/free-subscription/generate-single`);
  const config = getRestConfig();

  try {
    const { data } = await axios.post(
      endpoint,
      { ...dto, sourceClassification: SourceClassification.FreeSubscription },
      config,
    );

    return data;
  } catch (error) {
    mapError(error);
  }
};

const fetchWorkoutComments = async (workoutId: number) => {
  const endpoint = getCreatorApiUrl(`/admin-workouts/${workoutId}/posts`);
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const fetchAvailableWorkoutTags = async () => {
  const endpoint = getCreatorApiUrl(`/admin-workouts-tags`);
  const config = getRestConfig();

  try {
    const { data } = await axios.get(endpoint, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const attachWorkoutTags = async (workoutId: number, tags: Tag[] = []) => {
  const endpoint = getCreatorApiUrl(`/admin-workouts-tags/${workoutId}`);
  const config = getRestConfig();

  try {
    const { data } = await axios.post(endpoint, tags, config);

    return data;
  } catch (error) {
    mapError(error);
  }
};

const exports = {
  getInfluencers,
  upsertInfluencer,
  deleteInfluencer,
  getWorkout,
  upsertWorkout,
  deleteWorkout,
  archiveWorkout,
  cloneWorkout,
  getPortalUsers,
  upsertPortalUser,
  deletePortalUser,
  upload,
  getProgramList,
  getProgram,
  upsertProgram,
  reorderProgram,
  upsertCustomColorSchema,
  getConfigFile,
  updateConfigFile,
  generateFreeSubscription,
  fetchWorkoutComments,
  fetchAvailableWorkoutTags,
  attachWorkoutTags,
};

export default exports;
export {
  getInfluencers,
  upsertInfluencer,
  deleteInfluencer,
  getWorkout,
  upsertWorkout,
  deleteWorkout,
  archiveWorkout,
  cloneWorkout,
  getPortalUsers,
  upsertPortalUser,
  deletePortalUser,
  upload,
  getProgramList,
  getProgram,
  upsertProgram,
  reorderProgram,
  upsertCustomColorSchema,
  getConfigFile,
  updateConfigFile,
  generateFreeSubscription,
  fetchWorkoutComments,
  fetchAvailableWorkoutTags,
  attachWorkoutTags,
};
